<script lang="ts">
/**
 * Phase 17.12: the notification bell. Recent critical and warning problems (broken content, offline or out-of-date
 * players, …) with the unread count; opening it marks them read. A NEW critical problem that opens while the CMS is
 * open also pops up as a toast with a link to it. Polls once a minute while the tab is visible.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { HealthIssue, HealthNotifications } from '@codot/contracts';
import { healthApi } from '../api/health.api';
import { toast } from '../stores/toast';
import { CODE_LABEL, fixLink, issueMessage, SEVERITY, since } from '../utils/health';
import ActionMenu from './ActionMenu.vue';

/**
 * Issue ids already toasted (or present at first load): a toast is only for problems that open while you're here.
 * Shared by every bell on the page (the layout mounts one in the sidebar and one in the mobile header), so each
 * problem pops up once.
 */
const known = new Set<string>();
let first = true;
/** One poll for every bell on the page (they show the same list). */
const shared = ref<HealthNotifications | null>(null);
let users = 0;
let poller: ReturnType<typeof setInterval> | undefined;
</script>

<script setup lang="ts">
import { openIssue } from '../stores/issue-panel';
const props = withDefaults(defineProps<{ placement?: 'sidebar' | 'header' }>(), { placement: 'sidebar' });
const { t } = useI18n();
const router = useRouter();

const data = shared;
const open = ref(false);
const anchor = ref<HTMLElement | null>(null);

async function refresh(): Promise<void> {
  try {
    const next = await healthApi.notifications();
    if (!first) {
      for (const i of next.items) {
        if (!known.has(i.id) && i.severity === 'critical' && !i.resolvedAt) {
          toast(`${i.subjectName}: ${issueMessage(i)}`, { tone: 'error', ms: 10_000, action: { label: t('notify.viewIssue'), run: () => void (openIssue.value = i) } });
        }
      }
    }
    for (const i of next.items) known.add(i.id);
    first = false;
    data.value = next;
  } catch {
    /* a hint, never an error */
  }
}

onMounted(() => {
  if (users++ > 0) return;
  void refresh();
  poller = setInterval(() => document.visibilityState === 'visible' && void refresh(), 60_000);
});
onBeforeUnmount(() => {
  if (--users === 0) clearInterval(poller);
});

async function toggle(): Promise<void> {
  open.value = !open.value;
  if (open.value && data.value?.unread) {
    data.value = { ...data.value, unread: 0, seenAt: new Date().toISOString() };
    await healthApi.markSeen().catch(() => undefined);
  }
}
/** An open problem opens its side panel (what's wrong, and the actions that fix it); a fixed one goes to its page. */
function go(i: HealthIssue): void {
  open.value = false;
  if (!i.resolvedAt) openIssue.value = i;
  else void router.push(fixLink(i).to);
}
const isNew = (i: HealthIssue) => !i.resolvedAt && (!data.value?.seenAt || Date.parse(i.openedAt) > Date.parse(data.value.seenAt));
const badge = computed(() => (data.value?.unread ? (data.value.unread > 9 ? '9+' : String(data.value.unread)) : ''));
</script>

<template>
  <div class="relative" data-bell>
    <button
      ref="anchor"
      type="button"
      class="relative flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/10 hover:text-white"
      :class="props.placement === 'header' ? 'text-slate-300' : 'text-slate-400'"
      :aria-label="data?.unread ? t('notify.bellUnread', { n: data.unread }) : t('notify.bell')"
      :aria-expanded="open"
      aria-haspopup="dialog"
      @click.stop="toggle"
    >
      <svg viewBox="0 0 20 20" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 8a5 5 0 0 1 10 0c0 4 1.5 5.5 1.5 5.5h-13S5 12 5 8zM8.3 16.5a1.8 1.8 0 0 0 3.4 0" />
      </svg>
      <span
        v-if="badge"
        class="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-none font-semibold text-white ring-2 ring-[#0F172A]"
        aria-hidden="true"
        >{{ badge }}</span
      >
    </button>
    <ActionMenu :open="open" :anchor="anchor" width="w-[22rem] max-w-[calc(100vw-1rem)]" marker="data-bell" align="start" @close="open = false">
      <div role="dialog" :aria-label="t('notify.title')" class="text-sm">
        <div class="flex items-center border-b border-slate-200 px-3 py-2 dark:border-slate-800">
          <p class="flex-1 font-semibold">{{ t('notify.title') }}</p>
          <RouterLink :to="{ name: 'health-check' }" class="text-xs text-indigo-600 hover:underline dark:text-indigo-400" @click="open = false">{{ t('notify.all') }}</RouterLink>
        </div>
        <p v-if="!data?.items.length" class="px-3 py-8 text-center text-xs text-slate-500">{{ t('notify.empty') }}</p>
        <ul v-else class="max-h-96 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
          <li v-for="i in data.items" :key="i.id">
            <button type="button" class="flex w-full gap-2.5 px-3 py-2.5 text-start hover:bg-slate-50 dark:hover:bg-slate-800/60" @click="go(i)">
              <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full" :class="i.resolvedAt ? 'bg-emerald-500' : SEVERITY[i.severity].dot" aria-hidden="true" />
              <span class="min-w-0 flex-1">
                <span class="flex items-baseline gap-2">
                  <span class="min-w-0 flex-1 truncate font-medium" :class="i.resolvedAt ? 'text-slate-500' : ''">{{ i.subjectName }}</span>
                  <span v-if="isNew(i)" class="shrink-0 text-[10px] font-semibold text-indigo-600 uppercase dark:text-indigo-400">{{ t('notify.new') }}</span>
                  <span class="shrink-0 text-[11px] text-slate-400">{{ since(i.openedAt) }}</span>
                </span>
                <span class="block text-xs text-slate-500">{{ CODE_LABEL[i.code] }}<template v-if="i.resolvedAt"> · {{ t('notify.fixed') }}</template></span>
                <span class="mt-0.5 line-clamp-2 block text-xs text-slate-600 dark:text-slate-300">{{ issueMessage(i) }}</span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </ActionMenu>
  </div>
</template>
